import type { ReactNode } from 'react'
import { Activity, BarChart3, DoorOpen, Globe2, Radar, ShieldAlert } from 'lucide-react'
import type {
  ThreatMapCoverageData,
  ThreatMapCoverageStage,
  ThreatMapData,
} from '@/types/map'
import { Globe } from './Globe'
import './map-console.css'

export interface SocWallboardModeProps {
  data: ThreatMapData
  coverage?: ThreatMapCoverageData
  coverageLoading: boolean
  coverageError: unknown
  rangeDays: number
  feedAgeMs: number
  onExit: () => void
  onDrillCountry: (iso2: string) => void
}

const fmt = new Intl.NumberFormat('en-GB')

const QUEUE_LABELS: Array<[keyof ThreatMapCoverageData['queues'], string]> = [
  ['actorMissing', 'Needs attribution'],
  ['targetNameMissing', 'Needs target identity'],
  ['noRenderableGeo', 'Needs victim geo'],
  ['sourceFallbackOnly', 'Source fallback only'],
  ['geoReadFlagDisabled', 'Geo read disabled'],
  ['ransomwareGroupActorUnlinked', 'Ransomware group actor-unlinked'],
]

const STAGE_LABELS: Array<[keyof ThreatMapCoverageData['stages'], string]> = [
  ['actor', 'Attribution'],
  ['target', 'Target identity'],
  ['geo', 'Renderable geo'],
  ['ontology', 'Ontology'],
  ['actionability', 'Actionability'],
]

export function SocWallboardMode({
  data,
  coverage,
  coverageLoading,
  coverageError,
  rangeDays,
  feedAgeMs,
  onExit,
  onDrillCountry,
}: SocWallboardModeProps) {
  const total = coverage?.totals.incidents ?? 0
  const stale = feedAgeMs >= 90_000
  const coverageUnavailable = !coverage && !coverageLoading

  return (
    <section
      data-testid="soc-wallboard"
      className="soc-stage grid h-full min-h-0 grid-rows-[auto_1fr] overflow-hidden bg-[color:var(--surface-primary)]"
      aria-label="SOC wallboard threat posture"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--interactive-primary)] text-[color:var(--interactive-primary)]">
            <Radar className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-tertiary)]">
              SOC View · projection-owned wallboard
            </p>
            <h2 className="font-heading text-xl font-semibold text-[color:var(--text-primary)]">
              Live Map threat posture
            </h2>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-tertiary)]">
          <span>Window -{rangeDays}d</span>
          <span
            className="inline-flex items-center gap-1.5"
            style={{ color: stale ? 'var(--severity-high)' : 'var(--status-active)' }}
          >
            <span
              className={`h-2 w-2 rounded-full ${stale ? 'animate-pulse' : ''}`}
              style={{ background: stale ? 'var(--severity-high)' : 'var(--status-active)' }}
              aria-hidden
            />
            {stale ? 'Feed stale' : 'Feed OK'}
          </span>
          <button
            type="button"
            onClick={onExit}
            className="soc-accent-btn inline-flex items-center gap-1.5 rounded-md border border-[color:var(--interactive-primary)] px-2.5 py-1.5 font-medium text-[color:var(--interactive-primary)] transition-colors"
          >
            <DoorOpen className="h-3.5 w-3.5" aria-hidden />
            Exit SOC View
          </button>
        </div>
      </header>

      <div className="grid min-h-0 grid-cols-[minmax(260px,0.82fr)_minmax(420px,1.4fr)_minmax(280px,0.9fr)] gap-3 overflow-hidden p-3 max-[1200px]:grid-cols-1 max-[1200px]:overflow-y-auto">
        <aside className="flex min-h-0 flex-col gap-3 overflow-hidden max-[1200px]:overflow-visible">
          <CoverageStateBanner
            coverage={coverage}
            coverageLoading={coverageLoading}
            coverageError={coverageError}
          />
          <div className="grid grid-cols-2 gap-3 max-[1200px]:grid-cols-4 max-[800px]:grid-cols-2">
            <MetricCard
              label="Incidents"
              value={coverage ? fmt.format(coverage.totals.incidents) : '—'}
              detail="Aggregate-only cohort"
              icon={<Activity className="h-4 w-4" aria-hidden />}
            />
            <MetricCard
              label="Known actors"
              value={coverage ? formatPercent(coverage.stages.actor.projectedCount, total) : '—'}
              detail={
                coverage
                  ? `${fmt.format(coverage.stages.actor.projectedCount)} canonical`
                  : 'Coverage pending'
              }
              icon={<ShieldAlert className="h-4 w-4" aria-hidden />}
            />
            <MetricCard
              label="Renderable geo"
              value={coverage ? formatPercent(coverage.stages.geo.renderableCount ?? 0, total) : '—'}
              detail={coverage ? `${fmt.format(coverage.stages.geo.gapCount)} geo gaps` : 'Coverage pending'}
              icon={<Globe2 className="h-4 w-4" aria-hidden />}
            />
            <MetricCard
              label="Actionability"
              value={coverage ? formatPercent(coverage.stages.actionability.projectedCount, total) : '—'}
              detail={
                coverage
                  ? `${fmt.format(coverage.stages.actionability.gapCount)} not actionable`
                  : 'Coverage pending'
              }
              icon={<BarChart3 className="h-4 w-4" aria-hidden />}
            />
          </div>
          <StagePanel coverage={coverage} />
        </aside>

        <main className="soc-panel relative min-h-0 overflow-hidden" aria-label="SOC globe">
          <Globe
            data={data}
            onDrillCountry={onDrillCountry}
            isLoading={false}
            error={null}
          />
        </main>

        <aside className="flex min-h-0 flex-col gap-3 overflow-hidden max-[1200px]:overflow-visible">
          <QueuePanel coverage={coverage} />
          <ClusterPanel
            title="Source mix"
            empty="No source-kind counts"
            rows={coverage ? Object.entries(coverage.sourceKinds) : []}
          />
          <ClusterPanel
            title="Unknown reasons"
            empty="No unknown reasons"
            rows={coverage ? Object.entries(coverage.unknownReasons) : []}
          />
        </aside>
      </div>

      {coverageUnavailable ? (
        <p className="sr-only" role="status">
          SOC View is waiting for aggregate coverage metrics; it is not deriving posture from raw incident rows.
        </p>
      ) : null}
    </section>
  )
}

function CoverageStateBanner({
  coverage,
  coverageLoading,
  coverageError,
}: {
  coverage?: ThreatMapCoverageData
  coverageLoading: boolean
  coverageError: unknown
}) {
  if (coverageLoading && !coverage) {
    return (
      <section className="soc-panel p-3" aria-busy="true">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
          Loading aggregate coverage
        </p>
        <div className="mt-3 h-2 rounded-full bg-[color:var(--surface-tertiary)]" />
      </section>
    )
  }

  if (coverageError || !coverage) {
    return (
      <section className="soc-panel p-3" role="status">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--severity-high)]">
          Coverage unavailable
        </p>
        <p className="mt-2 text-xs text-[color:var(--text-tertiary)]">
          Wallboard posture is intentionally withheld until the aggregate coverage endpoint responds.
        </p>
      </section>
    )
  }

  return (
    <section className="soc-panel p-3" data-testid="soc-coverage-safety">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
        {coverage.coverageVersion} · aggregate only
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-[color:var(--text-secondary)]">
        <FlagPill
          label="Raw payload"
          on={!coverage.safety.rawPayloadIncluded}
          goodLabel="excluded"
          badLabel="included"
        />
        <FlagPill
          label="Geo map read"
          on={coverage.flags.geoMapLayersReadEnabled}
          goodLabel="enabled"
          badLabel="disabled"
        />
        <FlagPill
          label="Inferred impact"
          on={coverage.flags.inferredImpactReadEnabled}
          goodLabel="enabled"
          badLabel="disabled"
        />
        <FlagPill label="Safety" on={coverage.safety.aggregateOnly} goodLabel="aggregate" badLabel="raw" />
      </div>
    </section>
  )
}

function FlagPill({
  label,
  on,
  goodLabel,
  badLabel,
}: {
  label: string
  on: boolean
  goodLabel: string
  badLabel: string
}) {
  return (
    <span className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-primary)] px-2 py-1">
      <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
        {label}
      </span>
      <span
        className="font-mono text-[10px] uppercase tracking-[0.12em]"
        style={{ color: on ? 'var(--status-active)' : 'var(--text-tertiary)' }}
      >
        {on ? goodLabel : badLabel}
      </span>
    </span>
  )
}

function MetricCard({
  label,
  value,
  detail,
  icon,
}: {
  label: string
  value: string
  detail: string
  icon: ReactNode
}) {
  return (
    <section className="soc-panel p-3" data-testid="soc-metric-card">
      <div className="flex items-center justify-between gap-2 text-[color:var(--text-tertiary)]">
        <span className="font-mono text-[9px] uppercase tracking-[0.16em]">{label}</span>
        {icon}
      </div>
      <p className="mt-2 font-heading text-3xl font-semibold text-[color:var(--text-primary)]">
        {value}
      </p>
      <p className="mt-1 text-xs text-[color:var(--text-tertiary)]">{detail}</p>
    </section>
  )
}

function StagePanel({ coverage }: { coverage?: ThreatMapCoverageData }) {
  return (
    <section className="soc-panel min-h-0 overflow-hidden">
      <header className="soc-panel-head px-3 py-2">
        <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
          Projection coverage
        </h3>
      </header>
      <div className="space-y-2 p-3">
        {coverage ? (
          STAGE_LABELS.map(([key, label]) => (
            <StageRow
              key={key}
              label={label}
              stage={coverage.stages[key]}
              total={coverage.totals.incidents}
            />
          ))
        ) : (
          <p className="text-xs text-[color:var(--text-tertiary)]">Coverage metrics pending.</p>
        )}
      </div>
    </section>
  )
}

function StageRow({
  label,
  stage,
  total,
}: {
  label: string
  stage: ThreatMapCoverageStage
  total: number
}) {
  const count = stage.renderableCount ?? stage.projectedCount
  const pct = total > 0 ? Math.round((count / total) * 100) : 0

  return (
    <div>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-[color:var(--text-secondary)]">{label}</span>
        <span className="font-mono text-[color:var(--text-tertiary)]">
          {fmt.format(count)} / {fmt.format(total)} · {pct}%
        </span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[color:var(--surface-tertiary)]">
        <div
          className="h-full rounded-full bg-[color:var(--interactive-primary)]"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function QueuePanel({ coverage }: { coverage?: ThreatMapCoverageData }) {
  return (
    <section className="soc-panel min-h-0 overflow-hidden">
      <header className="soc-panel-head flex items-center justify-between px-3 py-2">
        <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
          Coverage queues
        </h3>
        {coverage ? (
          <span className="font-mono text-[10px] text-[color:var(--text-tertiary)]">
            N={fmt.format(Object.values(coverage.queues).reduce((sum, value) => sum + value, 0))}
          </span>
        ) : null}
      </header>
      <ul className="space-y-1.5 p-3">
        {coverage ? (
          QUEUE_LABELS.map(([key, label]) => (
            <li
              key={key}
              className="flex items-center justify-between gap-3 rounded-md bg-[color:var(--surface-primary)] px-2 py-1.5 text-xs"
            >
              <span className="text-[color:var(--text-secondary)]">{label}</span>
              <span className="font-mono text-[color:var(--text-primary)]">
                {fmt.format(coverage.queues[key])}
              </span>
            </li>
          ))
        ) : (
          <li className="text-xs text-[color:var(--text-tertiary)]">Coverage metrics pending.</li>
        )}
      </ul>
    </section>
  )
}

function ClusterPanel({
  title,
  rows,
  empty,
}: {
  title: string
  rows: Array<[string, number]>
  empty: string
}) {
  const sorted = [...rows].sort((a, b) => b[1] - a[1]).slice(0, 5)

  return (
    <section className="soc-panel min-h-0 overflow-hidden">
      <header className="soc-panel-head px-3 py-2">
        <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
          {title}
        </h3>
      </header>
      {sorted.length > 0 ? (
        <ul className="space-y-1.5 p-3">
          {sorted.map(([name, count]) => (
            <li key={name} className="flex items-center justify-between gap-3 text-xs">
              <span className="truncate text-[color:var(--text-secondary)]">{formatKey(name)}</span>
              <span className="font-mono text-[color:var(--text-primary)]">{fmt.format(count)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="p-3 text-xs text-[color:var(--text-tertiary)]">{empty}</p>
      )}
    </section>
  )
}

function formatPercent(part: number, total: number): string {
  if (total <= 0) return '0%'
  return `${Math.round((part / total) * 100)}%`
}

function formatKey(key: string): string {
  return key.replace(/_/g, ' ')
}
